<template>
  <NButton class="fixed bottom-5 right-5 z-1000" type="primary" @click="showForm = !showForm">
    {{ t('canvasUI.CThemeEditor.editColors') }}
  </NButton>
  <NCard v-if="showForm" class="fixed bottom-15 right-5 z-1000 w-80">
    <input ref="importJsonInput" class="invisible" type="file" @change="importJsonPalette" />
    <NSpace vertical>
      <NForm class="max-h-80vh overflow-y-auto">
        <NFormItem v-for="(_, key) in filteredPalette" :key="key" :label="key as string">
          <NColorPicker
            v-model:value="filteredPalette[key]"
            :modes="['hex']"
            :show-alpha="false"
            :updateInputModelOnDrop="true"
            @update:value="updateColor(key as keyof BasicPalette, $event)"
          />
        </NFormItem>
        <NFormItem>
          <NButton type="primary" @click="importJsonInput?.click()">
            {{ t('canvasUI.CThemeEditor.importJson') }}
          </NButton>
        </NFormItem>
        <NFormItem>
          <NButton type="primary" @click="exportJsonPalette">
            {{ t('canvasUI.CThemeEditor.exportJson') }}
          </NButton>
        </NFormItem>
      </NForm>
    </NSpace>
  </NCard>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { NColorPicker, NSpace, NForm, NFormItem, NButton, NCard } from 'naive-ui'
import { useI18n } from 'vue-i18n'

import { lightPalette } from '@lib/theme/light'
import type { BasicPalette } from '@lib/theme/theme.types'

const showForm = ref(false)
const filterKeys = ['boxShadow', 'boxShadow2', 'boxShadow3']
const { t } = useI18n()
const importJsonInput = ref<HTMLInputElement | null>(null)

const filteredPalette = computed(() => {
  return Object.fromEntries(Object.entries(lightPalette.value).filter(([key]) => !filterKeys.includes(key)))
})

function isValidHexColor(color: string): boolean {
  return /^#([0-9A-F]{3}){1,2}$/i.test(color)
}

function updateColor(key: keyof BasicPalette, color: string) {
  if (isValidHexColor(color)) {
    lightPalette.value[key] = color
    document.documentElement.style.setProperty(`--p-${key}`, color)
  }
}

async function importJsonPalette(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = () => {
      try {
        const json = JSON.parse(reader.result as string)
        for (const key in json) {
          if (Object.prototype.hasOwnProperty.call(lightPalette.value, key) && isValidHexColor(json[key])) {
            lightPalette.value[key as keyof BasicPalette] = json[key]
          }
        }
      } catch (error) {
        console.error('Invalid JSON file:', error)
      }
    }
    reader.readAsText(file)
  }
}

function exportJsonPalette() {
  const dataStr = JSON.stringify(filteredPalette.value)
  const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr)
  const linkElement = document.createElement('a')
  linkElement.setAttribute('href', dataUri)
  linkElement.setAttribute('download', 'palette.json')
  linkElement.click()
}
</script>
